

import React, {useState, useContext, useEffect} from "react"
import { TextField, InputAdornment, Select, MenuItem, FormHelperText } from "@material-ui/core"
import { BrowserView, MobileView, isBrowser } from "react-device-detect"
import FlexView from "react-flexview/lib"
import { Controller, useForm } from 'react-hook-form'
import { SessionContextStore } from "../../../context/SessionContext"
import { UserProfileContextStore, ACTIONS } from "../../../context/UserProfileContext"
import { OptionalTag } from "../../Core/OptionalTag"
import { UserApi } from "../../../api/user.api"
import { EventEmitterService, EventKey } from "../../../services/EventEmitterService"
import '../UserProfile.scss'
import { Action } from "../../../models/Action"
import { SessionService } from "../../../services/SessionService"
import StorageService from "../../../services/StorageService"
import { STATE_EXPIRY_TTL, COUNTRIES, POBOX_REGEX } from "../../../Constants"
import { FirebaseService, FirebaseConfigKey } from "../../../services/FirebaseService"
import { showError, awaitWithLoading } from "../../../services/utils"
import LocalizationService from "../../../services/LocalizationService"
import { ProposalSessionContextStore } from "../../../context/ProposalSessionContext"
import { useLocation } from "react-router-dom"
import { usePlacesWidget } from 'react-google-autocomplete';


export const AddressSection = (props) => {
    const location = useLocation()
    let sessionContext = useContext(SessionContextStore)
    const profileContext = useContext(UserProfileContextStore)
    const [states, setStates] = useState([])
    const { register, watch, triggerValidation, setValue, errors, control, getValues } = useForm(SessionService.isLoggedIn() ?
     { defaultValues: {
         address_line_1: sessionContext.user.address1,
         address_line_2: sessionContext.user.address2,
         city: sessionContext.user.city,
         zipcode: sessionContext.user.zipCode,
         state: sessionContext.user.state ? sessionContext.user.state : FirebaseService.getValue(FirebaseConfigKey.COUNTRY) == COUNTRIES.UK ? COUNTRIES.UK : 'statedefault',
     }, mode: 'onChange' } : { mode: 'onChange'})
    
    const address1Watch = watch('address_line_1', '')
    const address2Watch = watch('address_line_2', '')
    const cityWatch = watch('city', '')
    const zipcodeWatch = watch('zipcode', '')
    const stateWatch = watch('state')
    const zipcodeRegex = new RegExp(FirebaseService.getValue(FirebaseConfigKey.ZIPCODE_REGEX))
    const restrictedCountries: any[] = FirebaseService.getArray(FirebaseConfigKey.RESTRICTED_COUNTRIES)
    
    const [isAddress1Shrink, setIsAddress1Shrink] = React.useState(false);

    useEffect(()=>{
        setIsAddress1Shrink(!(address1Watch === "" || !address1Watch))
    },[address1Watch])


    const { ref: materialRef } = usePlacesWidget({
        apiKey: process.env.REACT_APP_GOOGLE_PLACE_API_KEY,
        onPlaceSelected: (place) => {
            const address = place['address_components'];
            const address_types = [];
            register({ 
                    required: 'Required', 
                    validate: (x) => {
                        if(!POBOX_REGEX.test(x) && getValues('state')=='FL' ) {
                            return "Please provide a physical address"
                        } 
                    }
                })
            setValue('address', '');
            for (const [i, address] of place['address_components'].entries()) {
                if (address.hasOwnProperty('types')) {
                    address_types[address['types'][0]] = {
                        short_name: address['short_name'],
                        long_name: address['long_name']
                    };
                }
            }
            setValue(
                'address_line_1',
                address_types.hasOwnProperty('route')
                    ? address_types.hasOwnProperty('street_number')
                        ? address_types['street_number']['long_name'] +
                          ' ' +
                          address_types['route']['long_name']
                        : address_types['route']['long_name']
                    : ''
            );
            setValue(
                'city',
                address_types.hasOwnProperty('locality')
                    ? address_types['locality']['long_name']
                    : address_types.hasOwnProperty('sublocality_level_1')
                    ? address_types['sublocality_level_1']['long_name']
                    : ''
            );
            setValue(
                'state',
                address_types.hasOwnProperty('administrative_area_level_1')
                    ? address_types['administrative_area_level_1']['short_name']
                    : ''
            );
            setValue(
                'zipcode',
                address_types.hasOwnProperty('postal_code')
                    ? address_types['postal_code']['short_name']
                    : ''
            );
        },
        inputAutocompleteValue: 'street_address',
        options: {
            componentRestrictions: {
                country: [process.env.REACT_APP_AUTO_COMPLETE_ADDRESS_COUNTRY]
            },
            types: ['geocode', 'establishment']
        },
        selectProps: {
            placeholder: 'here is the placeholder text'
        }
    });
    

    const setStateFromAutocomplete = (autoState) => {
        let selected = states.filter(i => i.name.toLowerCase() == autoState.toLowerCase())
        if(selected.length > 0)
            setValue('state', selected[0].code)
    }


    React.useEffect(() => {
        const fetchData = async () => {
            let res = await UserApi.getStates()

            if(res.ok) {
                setStates(res.parsedData)
                StorageService.setWithExpiry('STATE_LIST', res.parsedData, STATE_EXPIRY_TTL)
            }
            else {
                EventEmitterService.dispatch(EventKey.ShowError, res)
            }
        }

        if(StorageService.getWithExpiry('STATE_LIST')) {
            setStates(StorageService.getWithExpiry('STATE_LIST'))
        }
        else {
            fetchData()
        }

        let id = EventEmitterService.subscribe(EventKey.USER_PROFILE_SAVE, async () => {
            let valid = await triggerValidation()
            if(valid) {
                profileContext.dispatch(new Action(ACTIONS.SAVE_ADDRESS, getValues()))
            } else {
                profileContext.setError("Check for required fields/invalid data")
            }
        })

        return function cleanup() {
            EventEmitterService.unsubscribe(EventKey.USER_PROFILE_SAVE, id)
        }
    }, [])
    const [isAddress1Filled,setIsAddress1Filled] = React.useState(!!address1Watch);

    React.useEffect(() => {
        setValue('address_line_1', sessionContext.user.address1)
        setValue('address_line_2', sessionContext.user.address2)
        setValue('city', sessionContext.user.city,)
        setValue('zipcode', sessionContext.user.zipCode)
        if(FirebaseService.getValue(FirebaseConfigKey.COUNTRY) == COUNTRIES.UK &&
            (sessionContext.user.state == 'statedefault' || !sessionContext.user.state)) {
            setValue('state', 'UK')
        }
        else {
            setValue('state', sessionContext.user.state)
        }
    }, [sessionContext.user])

    return (
        <React.Fragment>
            <div>
                <MobileView>
                    <div className="margin-top-medium">
                        <TextField InputLabelProps={{ shrink: !!address1Watch }} 
                        fullWidth variant="outlined"
                        label="Address line 1"  
                        name="address_line_1"
                        inputRef={register({ 
                            required: 'Required', 
                            validate: (x) => {
                            if(!POBOX_REGEX.test(x) && getValues('state')=='FL' ) {
                                return "Please provide a physical address"
                            } 
                        }
                        })}
                        helperText={errors?.address_line_1?.message}
                        error={!!errors.address_line_1} />
                        <TextField InputLabelProps={{ shrink: !!address2Watch }} fullWidth variant="outlined" label="Address line 2" className="margin-top-basic" name="address_line_2" InputProps={{endAdornment: <InputAdornment position="end"><OptionalTag /> </InputAdornment>}} inputRef={register} error={!!errors.address_line_2} />
                    </div>    
                </MobileView>
                <BrowserView>
                    <FlexView className="margin-top-medium">
                        <Controller
                        control={control}
                        name="address_line_1" 
                        as={<TextField 
                        InputLabelProps={{ shrink: isAddress1Shrink }} 
                        fullWidth 
                        variant="outlined" 
                        label="Address line 1" 
                        name="address_line_1" 
                        inputRef={materialRef} 
                        error={!!errors.address_line_1}  
                        helperText={errors?.address_line_1?.message}  
                        onChange={(e)=>{
                            setValue('address_line_1', e.target.value)
                        }}
                        register={register({ 
                            required: 'Required', 
                            validate: (x) => {
                            if(!POBOX_REGEX.test(x) && getValues('state')=='FL' ) {
                                return "Please provide a physical address"
                            } 
                        }
                        })}/>}/>
                        
                        <TextField InputLabelProps={{ shrink: !!address2Watch }} fullWidth variant="outlined" label="Address line 2" className="margin-left-basic" name="address_line_2" InputProps={{endAdornment: <InputAdornment position="end"><OptionalTag /> </InputAdornment>}} inputRef={register} error={!!errors.address_line_2} />
                    </FlexView>
                </BrowserView>
                <div className="margin-top-basic margin-bottom-basic">
                    <FlexView>
                        <TextField InputLabelProps={{ shrink: !!cityWatch }} fullWidth variant="outlined" label="City" name="city" helperText={!!errors.city && 'Required'} inputRef={register({required: true})} error={!!errors.city} />
                        <div className="full-width margin-right-basic">
                            <Controller
                            rules={{ required: true, validate: (x) => x && x !== 'statedefault' }}
                            control={control}
                            name="state"
                            defaultValue={
                                sessionContext.user.state
                                ? sessionContext.user.state
                                : FirebaseService.getValue(FirebaseConfigKey.COUNTRY) === COUNTRIES.UK
                                ? COUNTRIES.UK
                                : 'statedefault'
                            }
                            as={
                                <TextField
                                error={!!errors.state}
                                fullWidth
                                label={LocalizationService.STRINGS.state}
                                select
                                variant="outlined"
                                className="margin-left-basic"
                                >
                                <MenuItem disabled value={'statedefault'}>
                                    <div className="placeholder-text">
                                    {LocalizationService.STRINGS.state}
                                    </div>
                                </MenuItem>
                                {[...states, ...restrictedCountries]
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((item) => (
                                    <MenuItem key={item.code} value={item.code}>
                                        {item.name}
                                    </MenuItem>
                                    ))}
                                </TextField>
                            }
                            />
                            {errors.state && (
                            <FormHelperText error={!!errors.state} className="margin-left-basic">
                                {errors.state.type === 'validate' ? 'Not available' : 'Required'}
                            </FormHelperText>
                            )}

                        </div>
                        <TextField name="state" onChange={(e) => {setStateFromAutocomplete(e.target.value)}} style={{height: 0, width:0}} />
                        
                        {isBrowser && <TextField InputLabelProps={{ shrink: !!zipcodeWatch }} fullWidth variant="outlined" className="margin-left-basic" label={LocalizationService.STRINGS.zipcode} name="zipcode" inputRef={register({required: true, validate: x => zipcodeRegex.test(x)})} helperText={!!errors.zipcode && `Invalid ${LocalizationService.STRINGS.zipcode}`} error={!!errors.zipcode} /> }
                        
                    </FlexView>
                    <MobileView>
                        <TextField InputLabelProps={{ shrink: !!zipcodeWatch }} fullWidth variant="outlined" className="margin-top-basic" label={LocalizationService.STRINGS.zipcode} name="zipcode" inputRef={register({required: true, validate: x => zipcodeRegex.test(x)})} helperText={!!errors.zipcode && `Invalid ${LocalizationService.STRINGS.zipcode}`} error={!!errors.zipcode} />
                    </MobileView>
                </div>
            </div>
        </React.Fragment>
    )
}